import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function LoanRecalls({ teamId, onChanged }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState("");

    const load = useCallback(() => {
        api.get("/loans/recalls", { params: { team_id: teamId } })
            .then(({ data: d }) => setData(d))
            .catch(() => setData(null));
    }, [teamId]);

    useEffect(() => { load(); }, [load]);

    const run = async (key, confirmText, fn) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setBusy(key);
        setErr("");
        try {
            await fn();
            load();
            if (onChanged) onChanged();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(null);
        }
    };

    if (!data) return null;
    const { incoming = [], outgoing = [], candidates = [], window_open: open } = data;
    const showCandidates = open && candidates.length > 0;
    if (!showCandidates && incoming.length === 0 && outgoing.length === 0) return null;

    return (
        <div className="op-card mt-6 overflow-hidden border-l-2" style={{ borderLeftColor: "rgb(var(--tc-ff7a1a))" }} data-testid="loan-recalls">
            <div className="px-5 py-4 border-b border-t22252a bg-t0e1014">
                <div className="op-label text-tff7a1a">↩️ RICHIAMO PRESTITI SECCHI</div>
                <div className="op-mono text-[10px] text-t8f95a3 mt-1">
                    Gratuito · serve il consenso della squadra che ha il giocatore · solo a mercato aperto
                </div>
            </div>
            {err && <div className="px-5 py-2 bg-tff3b30/10 text-tff3b30 text-xs op-mono" data-testid="loan-recalls-error">{err}</div>}
            <ul className="divide-y divide-t16181c">
                {incoming.map((r) => (
                    <Row key={r.id} testId={`recall-incoming-${r.id}`}
                        title={`${r.origin_team_name} vuole richiamare ${r.player_name}`}
                        sub="Se accetti il giocatore torna subito alla sua squadra, senza soldi.">
                        <Btn primary disabled={busy === r.id} testId={`recall-accept-${r.id}`}
                            onClick={() => run(r.id, `Accetti di restituire ${r.player_name} a ${r.origin_team_name}?`,
                                () => api.post(`/loans/recalls/${r.id}/accept`))}>Accetta</Btn>
                        <Btn disabled={busy === r.id} testId={`recall-refuse-${r.id}`}
                            onClick={() => run(r.id, null, () => api.post(`/loans/recalls/${r.id}/refuse`))}>Rifiuta</Btn>
                    </Row>
                ))}
                {outgoing.map((r) => (
                    <Row key={r.id} testId={`recall-outgoing-${r.id}`}
                        title={`${r.player_name} · richiamo in attesa`}
                        sub={`In attesa della risposta di ${r.holder_team_name}`}>
                        <Btn disabled={busy === r.id} testId={`recall-cancel-${r.id}`}
                            onClick={() => run(r.id, null, () => api.post(`/loans/recalls/${r.id}/cancel`))}>Annulla</Btn>
                    </Row>
                ))}
                {showCandidates && candidates.filter((c) => !c.pending).map((c) => (
                    <Row key={c.entry_id} testId={`recall-candidate-${c.entry_id}`}
                        title={`${c.name} ${c.position ? `· ${c.position}` : ""}`}
                        sub={`In prestito secco a ${c.holder_team_name}`}>
                        <Btn primary disabled={busy === c.entry_id} testId={`recall-request-${c.entry_id}`}
                            onClick={() => run(c.entry_id, `Chiedere a ${c.holder_team_name} di restituire ${c.name}?`,
                                () => api.post("/loans/recalls", { entry_id: c.entry_id }))}>Richiama</Btn>
                    </Row>
                ))}
            </ul>
        </div>
    );
}

function Row({ title, sub, testId, children }) {
    return (
        <li className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={testId}>
            <div className="flex-1 min-w-0">
                <div className="text-sm text-tffffff truncate">{title}</div>
                <div className="op-mono text-[10px] text-t8f95a3">{sub}</div>
            </div>
            <div className="flex gap-2">{children}</div>
        </li>
    );
}

function Btn({ primary, children, testId, ...rest }) {
    return (
        <button type="button" data-testid={testId} {...rest}
            className={`op-btn !py-1 text-xs ${primary ? "op-btn-primary" : ""}`}>
            {children}
        </button>
    );
}
